import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import API from '@/lib/api'
import { computeIBANFromCzechBankAccount } from '@/lib/computeIBANFromCzechBankAccount'

import { FioBankCredentialsFormSchema } from './schema'

type FioBankCredentialsFormValues = z.infer<typeof FioBankCredentialsFormSchema>

export const FioBankCredentialsForm = ({ onSuccess }: { onSuccess: () => void }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const methods = useForm<FioBankCredentialsFormValues>({
    resolver: zodResolver(FioBankCredentialsFormSchema),
    defaultValues: {
      currency: 'CZK',
    },
  })
  const { handleSubmit } = methods
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank.fio',
  })

  const onValid = (values: FioBankCredentialsFormValues) => {
    setIsSubmitting(true)
    API.post('/api/v1/fio/accounts', {
      accountNumber: values.accountNumber,
      iban: values.iban.replace(/\s/g, ''),
      currency: values.currency,
      name: values.name,
      token: values.token,
    }).then(() => {
      toast.success(t('form.submit.success.title'), {
        duration: 3000,
        closeButton: true,
      })
      methods.reset()
      onSuccess()
    }).catch(() => {
      toast.error(t('form.submit.error.title'), {
        description: t('form.submit.error.description'),
        duration: 6000,
        closeButton: true,
      })
    }).finally(() => {
      setIsSubmitting(false)
    })
  }

  const handleAccountNumberPaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault()
    const clipboardData = event.clipboardData
    const pastedData = clipboardData.getData('text')
    let accountNumber = pastedData.replace(/\s/g, '')
    const bankCode = '2010'

    if (accountNumber.includes('/')) {
      const [number, ] = accountNumber.split('/')
      methods.setValue('accountNumber', number)
      accountNumber = number
    } else {
      methods.setValue('accountNumber', accountNumber)
    }

    const [prefix, number] = accountNumber.includes('-')
      ? accountNumber.split('-')
      : ['', accountNumber]

    const iban = computeIBANFromCzechBankAccount(prefix, number, bankCode)
    methods.setValue('iban', iban)

    return false
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid)}>
        <div className="space-y-4">
          <div className="space-y-4">
            <FormField
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.accountNumber.label')}</FormLabel>
                  <FormControl>
                    <InputGroup>
                      <InputGroupInput
                        {...field}
                        onPaste={handleAccountNumberPaste}
                      />
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>/ 2010</InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="iban"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('form.iban.label')}</FormLabel>
                  <FormControl>
                    <Input {...field} className="tabular" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.currency.label')}</FormLabel>
                <FormControl>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value)
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Vyberte měnu" />
                    </SelectTrigger>
                    <SelectContent>
                      {['CZK', 'EUR', 'USD'].map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('form.name.label')}
                  <span className="font-normal text-muted-foreground">{t('form.name.optional')}</span>
                </FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="token"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.token.label')}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {t('form.submit.label')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

FioBankCredentialsForm.displayName = 'FioBankCredentialsForm'
